const DEFAULT_SAMPLE_RATIO = 1;

/**
 * PGS-3525: server-side (Next.js Node runtime) counterpart of `otelConfig.ts`.
 * Config values only, no SDK imports, so it stays cheap to import from
 * `instrumentation.ts`, the Sentry server configs and the SSR Apollo clients.
 *
 * These are plain runtime env vars (the standard OTel names), not
 * `NEXT_PUBLIC_*`: nothing here is inlined at build time, so the endpoint and
 * ratio can differ per deployment without a rebuild. As in the browser, an
 * unset endpoint means tracing is off and nothing is registered.
 *
 * An unparseable ratio falls back to the default with a warning rather than
 * reaching `TraceIdRatioBasedSampler`, which treats NaN as 0 and would
 * silently drop every trace.
 */
export const getOtelServerConfig = () => {
  const raw = process.env.OTEL_TRACES_SAMPLER_ARG?.trim();
  let sampleRatio = DEFAULT_SAMPLE_RATIO;

  if (raw) {
    const parsed = Number(raw);

    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) {
      sampleRatio = parsed;
    } else {
      console.warn(
        `[otel] Ignoring invalid OTEL_TRACES_SAMPLER_ARG ${JSON.stringify(
          raw
        )}: expected a number between 0 and 1. Falling back to ${DEFAULT_SAMPLE_RATIO}.`
      );
    }
  }

  return {
    tracesEndpoint: process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT || "",
    sampleRatio,
  };
};

/**
 * Checks only the endpoint, not the full config, so that calling this from
 * every SSR request (`getServerTraceLinks` runs once or twice per request)
 * does not re-parse `OTEL_TRACES_SAMPLER_ARG` and re-warn about a malformed
 * value on every call. The ratio itself is only read once, in
 * `initOtelServer`.
 */
export const isOtelServerEnabled = (): boolean =>
  Boolean(process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT);
